import { filter } from 'ramda';
import React, { useCallback, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import FormContainer from '~/components/organism/ModalsV2/FormContainer';
import { withOverlay } from '~/components/organism/ModalsV2/Overlay';
import TextButton from '~/components/atom/TextButton';
import TEST_ID from './index.testid';

import ActionForm from './components/ActionForm';
import { ClientFlowAction } from '~/graphql/types.client';
import { ActionContext } from '~/components/page/Automation/v2/components/Builder/hooks/useActionContext';
import serializeEditorValues from './utils/serializeEditorValues';
import { initialFlowState } from '../../state';
import useBuilderContext from '../Builder/hooks/useBuilderContext';
import useRelativeMaps from '../Builder/hooks/useRelativeMaps';
import metadata from '../../state/metadata';
import { FlowV2_Update_Metadata } from '~/graphql/types';
import useGlobalKeyBinding from '~/hooks/useGlobalKeyBinding';
import useMountedRef from '~/hooks/useMountedRef';
import { MODAL_PORTAL_CLASS_NAME } from '~/hooks/usePortal';
import useKeybinding from '~/hooks/useKeybinding';
import { cardHeadings } from '../Builder/utils/getCardHeading';

export type Props = {
  action: ClientFlowAction;
  onConfirm: (updatedAction: ClientFlowAction) => void;

  /**
   * Fired when user clicks cancel or close.
   */
  onClose?: () => void;

  /** we provide a root for the Portal in overlay component if we want to portal to a specific root element */
  root?: string;
};

const actionHeaderMap: Record<ClientFlowAction['__typename'], string> =
  Object.freeze({
    FlowV2_Action_Start: cardHeadings.Start,
    FlowV2_Action_Task_Create: cardHeadings.Task_Create,
    FlowV2_Action_SendEmail_Plain: cardHeadings.SendEmail_Plain,
    FlowV2_Action_Contact_AddTag: cardHeadings.Contact_AddTag,
    FlowV2_Action_Contact_DeleteTag: cardHeadings.Contact_DeleteTag,
    FlowV2_Action_Wait: cardHeadings.Wait,
    FlowV2_Action_IfElse: cardHeadings.IfElse,
    FlowV2_Action_Contact_Details: cardHeadings.Contact_Details,
    FlowV2_Action_Contact_Assign: cardHeadings.Contact_Assign,
    FlowV2_Action_Realworks_SendContact: cardHeadings.Realworks_SendContact,
    FlowV2_Action_Zapier_Trigger: cardHeadings.Zapier_Trigger,
    FlowV2_Action_SendNotification: cardHeadings.SendNotification,
    FlowV2_Action_Trickle: cardHeadings.Trickle,
    FlowV2_Action_LFA_CreateRequest: cardHeadings.LFA_CreateRequest,
    FlowV2_Action_Stop_Flows: cardHeadings.Stop_Flows,
  });

const text = {
  unknown: 'Onbekend',
  cancel: 'Annuleren',
  save: 'Opslaan',
};

const UpdateAction: React.FCC<Props> = ({ action, onClose, onConfirm }) => {
  const [ref, setRef] = useMountedRef<HTMLDivElement>();
  const [changes, setChanges] = useState<ClientFlowAction>(action);
  const setFlowMetadata = useSetRecoilState(metadata);

  const relativeMaps = useRelativeMaps({
    actionId: action.id,
  });

  const { primitiveInput, primitiveListInput } = useBuilderContext();

  const initialFlow = useRecoilValue(initialFlowState);

  const onChange = useCallback((changes: ClientFlowAction) => {
    setChanges(changes);
  }, []);

  const onConfirmInternal = useCallback(() => {
    const serializedChanges = serializeEditorValues(changes);

    /**
     * When we are updating an existing (previously saved) Wait action set metadata accordingly
     * This is not needed for Trickle action
     */
    if (
      changes?.__typename === 'FlowV2_Action_Wait' &&
      initialFlow?.actions.find(act => act.id === serializedChanges.id)
    ) {
      if (changes.metadataAction) {
        setFlowMetadata(prev => {
          const selfFiltered = prev.filter(
            m => m.flowBlueprintActionId !== action.id,
          );
          return [
            ...selfFiltered,
            {
              flowBlueprintActionId: action.id,
              action: changes.metadataAction,
            } as FlowV2_Update_Metadata,
          ];
        });
      } else {
        // When the last change is removed from the action
        setFlowMetadata(prev =>
          filter(m => m.flowBlueprintActionId !== serializedChanges.id, prev),
        );
      }
    }

    onConfirm(serializedChanges);
    onClose && onClose();
  }, [
    action.id,
    changes,
    initialFlow?.actions,
    onClose,
    onConfirm,
    setFlowMetadata,
  ]);

  useGlobalKeyBinding({
    keys: 'escape',
    enabled: true,
    callback: () => {
      onConfirmInternal();
      onClose && onClose();
    },
  });

  useKeybinding({
    keys: 'enter',
    callback: () => {
      const allModals = document.querySelectorAll(
        `.${MODAL_PORTAL_CLASS_NAME}`,
      );
      const modalIds = Array.from(allModals).map(modal => modal.id);
      const otherModalsOpened = modalIds.length > 1;

      if (otherModalsOpened) {
        return;
      }

      onConfirmInternal();
      onClose && onClose();
    },
  });

  return (
    <ActionContext.Provider
      value={{ actionId: action.id, actionType: action.actionType }}
    >
      <FormContainer
        dataTestId={TEST_ID.CONTAINER}
        ref={setRef}
        header={actionHeaderMap[action.__typename] || text.unknown}
        onClose={() => {
          onConfirmInternal();
          onClose && onClose();
        }}
      >
        <ActionForm
          action={changes}
          onChange={onChange}
          outerContainer={ref}
          inputListPrimitives={primitiveListInput}
          inputPrimitives={primitiveInput}
          maps={relativeMaps}
        />
        <JustificationContainer
          justification="space-between"
          align="center"
          margin={['l', null, null]}
        >
          <TextButton
            label={text.cancel}
            onClick={onClose}
            padding={[null]}
            appearance="danger"
            type="button"
            dataTestId={TEST_ID.CANCEL_BUTTON}
          />
          <Button
            icon="check"
            appearance="secondary"
            label={text.save}
            type="submit"
            onClick={onConfirmInternal}
            dataTestId={TEST_ID.SAVE_BUTTON}
          />
        </JustificationContainer>
      </FormContainer>
    </ActionContext.Provider>
  );
};

const WrappedUpdateAction = withOverlay(UpdateAction);

export default props => (
  <WrappedUpdateAction {...props} root="update-action-modal" />
);
